import * as _ from 'lodash';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';
import emojiRegex from 'emoji-regex';
type Option = {
  message: string;
  min?: number;
  max?: number;
};

type validateType =
  | 'required'
  | 'email'
  | 'phone'
  | 'postCode'
  | 'password'
  | 'space'
  | 'number'
  | 'confirmPassword'
  | 'city'
  | 'streetLine'
  | 'building'
  | 'name'
  | 'isLength'
  | 'min8Characters'
  | 'max30Characters'
  | 'nameFull'
  | 'isPositive'
  | 'max100Characters';

type ValidatorOption = string | boolean | Option;
type Validators = { [key in validateType]?: ValidatorOption };

export const regexPosCode = /^\d{1,7}$/;
export const regexPass = /^.{8,30}$/;
export const regexOtpCode = /^\d{6}$/;
export const spaceRegex = /\s+/;
export const numberAlphabet = /^[a-zA-Z0-9\s'-]+$/u;
export const numberAndWord = /^[a-zA-Z0-9\s'-]*$/;
export const REGEX_PHONE_NUMBER = /^0\d{8,10}$/;
export const regexImage = /^image\/.*$/i;
export const regexMin8Characters = /^.{8,}$/;
export const regexMax30Characters = /^.{8,30}$/;
export const regexMax100Characters = /^.{1,101}$/;
const getMessage = (option: ValidatorOption): string => {
  return typeof option === 'object' ? option.message : (option as string);
};
const regexEmoj = emojiRegex();
const VALIDATOR: any = {
  required: (value: string | number | undefined | null, option: ValidatorOption) => {
    if (value === undefined || value === null || value.toString().trim() === '') {
      throw new Error(getMessage(option));
    }

    const type = typeof value;
    let isValid = true;
    if (type === 'string') {
      isValid = !!(value as string).trim();
    }

    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },
  password: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = regexPass.test(value.trim()) && !regexEmoj.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  min8Characters: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = regexMin8Characters.test(value.trim());
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  max30Characters: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = regexMax30Characters.test(value.trim());
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  max100Characters: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = regexMax100Characters.test(String(value).trim());
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  emoj: (value: string, option: ValidatorOption) => {
    const isValid = !regexEmoj.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  city: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = numberAlphabet.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  building: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = numberAlphabet.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  streetLine: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = numberAlphabet.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  space: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    if (spaceRegex.test(value)) {
      throw new Error(getMessage(option));
    }
  },

  email: (value: string, option: ValidatorOption) => {
    if (!value) {
      return;
    }
    const isValid = isEmail(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },
  phone: (value: string, option: ValidatorOption) => {
    const phoneNumber = value?.replace(/[-\s]/g, '');
    if (!REGEX_PHONE_NUMBER.test(phoneNumber)) {
      throw new Error(getMessage(option));
    }
  },

  postCode: (value: string, option: Option) => {
    const isValid = regexPosCode.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  name: (value: string, option: ValidatorOption) => {
    const isValid = numberAlphabet.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  nameFull: (value: string, option: ValidatorOption) => {
    const isValid = !regexEmoj.test(value);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  number: (value: string, option: Option) => {
    if (!value) {
      return;
    }
    const numberValue = +value;
    if (isNaN(+value) || !Number.isInteger(numberValue) || numberValue <= 0) {
      throw new Error(getMessage(option));
    }
  },

  confirmPassword: (value, option, originalValue) => {
    if (!value) {
      return;
    }
    if (value !== originalValue) {
      throw new Error(getMessage(option));
    }
  },

  isLength: (value: string, option: Option) => {
    const isValid = isLength(value, option);
    if (!isValid) {
      throw new Error(getMessage(option));
    }
  },

  isPositive: (value: string, option: Option) => {
    if (!value) {
      return;
    }
    if (isNaN(+value) || +value < 1 || +value % 1 !== 0) {
      throw new Error(getMessage(option));
    }
  },
};

export const _validator = (validators: Validators, originalValue?: string) => {
  return async (rule: any, text: string) => {
    _.map(validators, (options, type) => {
      options && VALIDATOR[type] && VALIDATOR[type](text, options, originalValue);
    });
  };
};
